import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { translate } from "../../../hooks/translate";
import { useModalConfirm as confirm } from "../../../hooks/useModalConfirm";
import { withAuth } from "../../../components/HOC/withAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import {
  dateStrInAppTz,
  formatInstantDateShortTr,
} from "../../../utils/appTime";
import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Lozenge from "../../../components/BaseUI/Lozenge";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiPackage,
  FiCheck,
  FiPlus,
  FiTrash2,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiTruck,
  FiShoppingCart,
  FiServer,
  FiBox,
} from "react-icons/fi";
import IconButton from "../../../components/BaseUI/Button/IconButton";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const toDeviceIdStr = (device) => {
  const raw = device?.deviceId ?? device?._id ?? device;
  if (!raw) return null;
  if (typeof raw === "object" && raw._id != null) return String(raw._id);
  return String(raw);
};

const StockTransferForm = () => {
  const { deliveryId: transferId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/deliveries",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [devices, setDevices] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [expandedModelsInDelivery, setExpandedModelsInDelivery] = useState(
    new Set(),
  );
  const [groupSelectedDevices, setGroupSelectedDevices] = useState(new Set());
  const [groupAssignMode, setGroupAssignMode] = useState(false);

  useTitle(
    transferId ? "Rovion MES - Sevkiyat Düzenle" : "Rovion MES - Yeni Sevkiyat",
  );

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Sevkiyatlar", url: "/deliveries" },
    { label: transferId ? "Düzenle" : "Yeni Sevkiyat" },
  ];

  const [formData, setFormData] = useState({
    saleId: "",
    companyId: "",
    notes: "",
    totalDevices: 0,
  });

  // Single delivery instead of multiple deliveries
  const now = new Date();
  const [delivery, setDelivery] = useState({
    id: "delivery-1",
    deliveryName: "",
    sequence: "",
    vehiclePlate: "",
    deliveredName: "",
    deliveryDate: dateStrInAppTz(now),
    deliveryTime: now.toTimeString().slice(0, 5), // HH:MM format
    deviceIds: [],
  });

  // Track original device IDs when editing to avoid double counting
  const [originalDeviceIds, setOriginalDeviceIds] = useState([]);

  const addToast = useCallback((message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  }, []);

  const removeToast = (index) => {
    setToasts((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    loadData();
  }, [transferId]);

  // Handle saleId from URL params
  useEffect(() => {
    const saleIdFromUrl = searchParams.get("saleId");
    if (saleIdFromUrl && !transferId) {
      setFormData((prev) => ({
        ...prev,
        saleId: saleIdFromUrl,
      }));
    }
  }, [searchParams, transferId]);

  // Handle pre-selected devices from StockProducts page
  useEffect(() => {
    if (!transferId && devices.length > 0 && delivery.deviceIds.length === 0) {
      const preSelectedDevicesStr = localStorage.getItem(
        "preSelectedDevicesForDelivery",
      );
      if (preSelectedDevicesStr) {
        try {
          const preSelectedDeviceIds = JSON.parse(preSelectedDevicesStr);
          if (
            Array.isArray(preSelectedDeviceIds) &&
            preSelectedDeviceIds.length > 0
          ) {
            // Filter to only include devices that exist in the devices list
            const validDeviceIds = preSelectedDeviceIds.filter((id) =>
              devices.some((d) => d._id === id.toString()),
            );

            if (validDeviceIds.length > 0) {
              // Assign pre-selected devices to single delivery
              setDelivery((prev) => ({
                ...prev,
                deviceIds: validDeviceIds.map((id) => id.toString()),
              }));
              addToast(
                `${validDeviceIds.length} ürün sevkiyata otomatik olarak atandı`,
                "success",
              );
            }
            // Clear localStorage after processing
            localStorage.removeItem("preSelectedDevicesForDelivery");
          }
        } catch (error) {
          console.error("Error parsing pre-selected devices:", error);
          localStorage.removeItem("preSelectedDevicesForDelivery");
        }
      }
    }
  }, [devices, transferId, delivery.deviceIds.length, addToast]);

  // Auto-generate delivery name: "<saleName>-<sequence>" (no padding)
  useEffect(() => {
    const selectedSale = sales.find((s) => s._id === formData.saleId);
    if (!selectedSale?.saleName || !delivery.sequence) return;

    const nextDeliveryName = `${selectedSale.saleName}-${delivery.sequence}`;
    setDelivery((prev) => ({ ...prev, deliveryName: nextDeliveryName }));
  }, [sales, formData.saleId, delivery.sequence]);

  const fetchDeliverySequenceForSale = async (saleId) => {
    const token = getToken();
    const response = await axios.get(`${API_URL}/api/deliveries`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const deliveriesList = response.data?.deliveries;
    const count = Array.isArray(deliveriesList)
      ? deliveriesList.filter((d) => {
          const saleRef =
            typeof d.saleId === "object" ? d.saleId?._id : d.saleId;
          return saleRef?.toString() === saleId?.toString();
        }).length
      : 0;
    return count + 1;
  };

  // Auto-generate sequence for new deliveries based on delivery count for this sale
  useEffect(() => {
    if (transferId) return; // editing: keep sequence parsed from existing deliveryName

    if (!formData.saleId) {
      setDelivery((prev) => ({ ...prev, sequence: "", deliveryName: "" }));
      return;
    }

    // Clear previous auto-generated values until we fetch the next sequence
    setDelivery((prev) => ({ ...prev, sequence: "", deliveryName: "" }));

    let cancelled = false;
    (async () => {
      try {
        const seq = await fetchDeliverySequenceForSale(formData.saleId);
        if (cancelled) return;
        setDelivery((prev) => ({ ...prev, sequence: seq.toString() }));
      } catch (e) {
        console.error("Failed to generate delivery sequence:", e);
        if (cancelled) return;
        setDelivery((prev) => ({ ...prev, sequence: "", deliveryName: "" }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.saleId, transferId]);

  // If sale is pre-selected (URL param), auto-select its company
  useEffect(() => {
    if (!formData.saleId || formData.companyId) return;

    const selectedSale = sales.find((s) => s._id === formData.saleId);
    const companyId =
      typeof selectedSale?.companyId === "object"
        ? selectedSale?.companyId?._id
        : selectedSale?.companyId;

    if (companyId) {
      setFormData((prev) => ({
        ...prev,
        companyId: companyId.toString(),
      }));
    }
  }, [sales, formData.saleId, formData.companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      await fetchSales();
      await fetchDevices();
      await fetchCompanies();

      if (transferId) {
        const token = getToken();
        const response = await axios.get(
          `${API_URL}/api/deliveries/${transferId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const transfer = response.data?.delivery || response.data;

        const transferDeliveryName =
          transfer.deliveryName || transfer.notes || "";
        // Extract trailing number from "<something>-<sequence>"
        const parsedSequence = (() => {
          const match = (transferDeliveryName || "").match(/(\d+)\s*$/);
          if (!match) return "";
          const num = parseInt(match[1], 10);
          if (!Number.isFinite(num) || num <= 0) return "";
          return num.toString();
        })();

        setFormData({
          saleId: transfer.saleId?._id || transfer.saleId || "",
          companyId:
            transfer.saleId?.companyId?._id || transfer.saleId?.companyId || "",
          notes: transfer.notes || "",
          totalDevices: transfer.totalDevices || 0,
        });

        // Extract and add devices from transfer to devices state
        const transferDevices = transfer.devices || [];
        const deviceObjects = [];

        transferDevices.forEach((device) => {
          const idStr = toDeviceIdStr(device);
          if (idStr) {
            // If device object is populated, add it
            if (device && typeof device === "object" && device.serialNumber) {
              deviceObjects.push({
                _id: idStr,
                serialNumber: device.serialNumber,
                modelId: device.modelId ?? device.deviceId?.modelId,
                model: device.modelId ?? device.deviceId?.modelId,
                status: device.status || device.deviceId?.status || "delivered",
                platform: device.platform ?? device.deviceId?.platform,
              });
            } else if (
              device?.deviceId &&
              typeof device.deviceId === "object" &&
              device.deviceId.serialNumber
            ) {
              deviceObjects.push({
                _id: idStr,
                serialNumber: device.deviceId.serialNumber,
                modelId: device.deviceId.modelId,
                model: device.deviceId.modelId,
                status: device.deviceId.status || "delivered",
                platform: device.deviceId.platform,
              });
            }
          }
        });

        // Add transfer devices to devices list if not already there
        if (deviceObjects.length > 0) {
          setDevices((prev) => {
            const existingIds = new Set(prev.map((d) => d._id));
            const newDevices = deviceObjects.filter(
              (d) => !existingIds.has(d._id),
            );
            return [...prev, ...newDevices];
          });
        }

        // Map transfer data directly to single delivery
        const allDeviceIds = transferDevices
          .map((device) => toDeviceIdStr(device))
          .filter(Boolean);

        const transferDeliveryDate = transfer.deliveryDate
          ? dateStrInAppTz(new Date(transfer.deliveryDate))
          : dateStrInAppTz(new Date());
        const transferDeliveryTime =
          transfer.deliveryTime || new Date().toTimeString().slice(0, 5);

        setDelivery({
          id: "delivery-1",
          deliveryName: transferDeliveryName,
          sequence: parsedSequence,
          vehiclePlate: transfer.vehiclePlate || "",
          deliveredName: transfer.driverName || "",
          deliveryDate: transferDeliveryDate,
          deliveryTime: transferDeliveryTime,
          deviceIds: allDeviceIds,
        });

        // Store original device IDs to avoid double counting
        setOriginalDeviceIds(allDeviceIds);

        // Keep backward compatibility fallback
        const firstBatch = (transfer.transferBatches || [])[0];
        if (firstBatch && !transfer.vehiclePlate && !transfer.driverName) {
          setDelivery((prev) => ({
            ...prev,
            vehiclePlate: firstBatch?.vehiclePlate || prev.vehiclePlate,
            deliveredName:
              firstBatch?.driverName ||
              firstBatch?.deliveredName ||
              prev.deliveredName,
            deliveryDate: prev.deliveryDate,
            deliveryTime: prev.deliveryTime,
            deviceIds: allDeviceIds,
          }));
        }
      } else {
        // Reset original device IDs when creating new delivery
        setOriginalDeviceIds([]);
      }
    } catch (error) {
      console.error("Error loading delivery data:", error);
      addToast(
        error.response?.data?.message ||
          "Sevkiyat verileri yüklenirken bir hata oluştu",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/device-sales`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSales(response.data?.sales || []);
    } catch (error) {
      console.error("Failed to fetch sales:", error);
    }
  };

  const fetchDevices = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/devices`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: "stock", getAll: true },
      });
      const allDevices = response.data?.devices || response.data || [];
      const stockDevices = Array.isArray(allDevices)
        ? allDevices.filter((d) => d.status === "stock")
        : [];
      setDevices(stockDevices);
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  // For the device assignment section - only show stock devices and filter by sale's models
  const filteredDevices = useMemo(() => {
    // If no sale selected, do not show any devices
    if (!formData.saleId) {
      return [];
    }

    // First filter by stock status
    const stockDevices = devices.filter((d) => d.status === "stock");

    // Filter by selected sale's models if a sale is selected
    let saleFilteredDevices = stockDevices;
    if (formData.saleId) {
      const selectedSale = sales.find((s) => s._id === formData.saleId);
      if (
        selectedSale?.models &&
        Array.isArray(selectedSale.models) &&
        selectedSale.models.length > 0
      ) {
        // Extract model IDs from the sale's models array
        const saleModelIds = selectedSale.models
          .map((model) => {
            // Handle both object and string modelId
            if (typeof model.modelId === "object" && model.modelId?._id) {
              return model.modelId._id.toString();
            }
            return model.modelId?.toString() || model.modelId;
          })
          .filter(Boolean);

        // Filter devices to only include those whose modelId matches one of the sale's model IDs
        saleFilteredDevices = stockDevices.filter((d) => {
          // Get device's modelId (handle both object and string)
          let deviceModelId = null;
          if (d.modelId) {
            if (typeof d.modelId === "object" && "_id" in d.modelId) {
              deviceModelId = d.modelId._id.toString();
            } else if (typeof d.modelId === "string") {
              deviceModelId = d.modelId;
            }
          }
          // Also check model._id if modelId is not available
          if (
            !deviceModelId &&
            d.model &&
            typeof d.model === "object" &&
            "_id" in d.model
          ) {
            deviceModelId = d.model._id.toString();
          }

          return deviceModelId && saleModelIds.includes(deviceModelId);
        });
      }
    }

    // Then filter by search query if provided
    if (!deviceSearch.trim()) return saleFilteredDevices;
    const search = deviceSearch.toLowerCase();
    return saleFilteredDevices.filter(
      (d) =>
        d.serialNumber?.toLowerCase().includes(search) ||
        d.model?.name?.toLowerCase().includes(search) ||
        d.modelId?.name?.toLowerCase().includes(search) ||
        d.platform?.name?.toLowerCase().includes(search),
    );
  }, [devices, deviceSearch, formData.saleId, sales]);

  const groupDevicesByModel = (deviceIds) => {
    const grouped = {};
    deviceIds.forEach((deviceId) => {
      const device = devices.find((d) => d._id === deviceId);
      if (device) {
        let modelKey = "unknown";
        if (
          device.modelId &&
          typeof device.modelId === "object" &&
          "_id" in device.modelId
        ) {
          modelKey = device.modelId._id;
        } else if (device.model?.name) {
          modelKey = device.model.name;
        }
        const modelName =
          device.modelId?.name || device.model?.name || "Bilinmeyen Model";

        if (!grouped[modelKey]) {
          grouped[modelKey] = { modelName, devices: [] };
        }
        grouped[modelKey].devices.push(device);
      }
    });
    return grouped;
  };

  const updateDeliveryField = (field, value) => {
    setDelivery((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanySelect = (company) => {
    if (!company?._id) return;
    setFormData((prev) => ({
      ...prev,
      companyId: company._id.toString(),
      saleId: "",
    }));
  };

  // Helper function to check if device can be assigned based on model limits
  const canAssignDevice = (deviceId) => {
    if (!formData.saleId) {
      return { canAssign: true };
    }

    const device = devices.find((d) => d._id === deviceId);
    if (!device) {
      return { canAssign: false, message: "Ürün bulunamadı" };
    }

    const selectedSale = sales.find((s) => s._id === formData.saleId);
    if (!selectedSale?.models || !Array.isArray(selectedSale.models)) {
      return { canAssign: true };
    }

    // Get device's modelId
    let deviceModelId = null;
    if (device.modelId) {
      if (typeof device.modelId === "object" && "_id" in device.modelId) {
        deviceModelId = device.modelId._id.toString();
      } else if (typeof device.modelId === "string") {
        deviceModelId = device.modelId;
      }
    }
    if (
      !deviceModelId &&
      device.model &&
      typeof device.model === "object" &&
      "_id" in device.model
    ) {
      deviceModelId = device.model._id.toString();
    }

    if (!deviceModelId) {
      return { canAssign: true }; // Allow if modelId can't be determined
    }

    // Find the model in sale's models
    const saleModel = selectedSale.models.find((model) => {
      const modelId =
        typeof model.modelId === "object" && model.modelId?._id
          ? model.modelId._id.toString()
          : model.modelId?.toString();
      return modelId === deviceModelId;
    });

    if (!saleModel) {
      return { canAssign: false, message: "Bu model siparişta bulunmuyor" };
    }

    const expectedCount = saleModel.count || 0;
    if (expectedCount === 0) {
      return { canAssign: true }; // No limit set
    }

    // Get previously delivered count for this model (excluding current delivery if editing)
    const modelId =
      typeof saleModel.modelId === "object" && saleModel.modelId?._id
        ? saleModel.modelId._id.toString()
        : saleModel.modelId?.toString();
    const previouslyDelivered = getPreviouslyDeliveredCount(
      modelId,
      saleModel.deliveredCount,
    );

    // Count currently assigned devices for this model in this delivery
    const currentlyAssignedForModel = delivery.deviceIds.filter((id) => {
      const assignedDevice = devices.find((d) => d._id === id);
      if (!assignedDevice) return false;
      let assignedDeviceModelId = null;
      if (assignedDevice.modelId) {
        if (
          typeof assignedDevice.modelId === "object" &&
          "_id" in assignedDevice.modelId
        ) {
          assignedDeviceModelId = assignedDevice.modelId._id.toString();
        } else if (typeof assignedDevice.modelId === "string") {
          assignedDeviceModelId = assignedDevice.modelId;
        }
      }
      if (
        !assignedDeviceModelId &&
        assignedDevice.model &&
        typeof assignedDevice.model === "object" &&
        "_id" in assignedDevice.model
      ) {
        assignedDeviceModelId = assignedDevice.model._id.toString();
      }
      return assignedDeviceModelId === deviceModelId;
    }).length;

    // Check if device is already assigned (for toggle behavior)
    const isAlreadyAssigned = delivery.deviceIds.includes(deviceId);

    // If removing, always allow
    if (isAlreadyAssigned) {
      return { canAssign: true };
    }

    // Calculate total (previous deliveries + current assignment)
    const totalAssigned = previouslyDelivered + currentlyAssignedForModel;

    // If adding, check if we're at or over the limit
    if (totalAssigned >= expectedCount) {
      const modelName =
        device.modelId?.name || device.model?.name || "Bu model";
      const remaining = expectedCount - previouslyDelivered;
      return {
        canAssign: false,
        message: `${modelName} için maksimum ${expectedCount} ürün atanabilir. ${previouslyDelivered} ürün daha önce teslim edildi, ${
          remaining > 0 ? `sadece ${remaining} ürün daha` : "daha fazla ürün"
        } eklenebilir.`,
      };
    }

    return { canAssign: true };
  };

  const toggleDeviceAssignment = (deviceId, forceAdd = false) => {
    // Check if we're adding (not removing)
    const isCurrentlyAssigned = delivery.deviceIds.includes(deviceId);

    // Only validate when adding a new device
    if (!isCurrentlyAssigned || forceAdd) {
      const validation = canAssignDevice(deviceId);
      if (!validation.canAssign) {
        addToast(validation.message || "Bu ürün atanamaz", "warning");
        return;
      }
    }

    setDelivery((prev) => {
      // If forceAdd is true, only add (don't remove if already exists)
      if (forceAdd) {
        return {
          ...prev,
          deviceIds: prev.deviceIds.includes(deviceId)
            ? prev.deviceIds
            : [...prev.deviceIds, deviceId],
        };
      }
      // Toggle behavior (default)
      return {
        ...prev,
        deviceIds: prev.deviceIds.includes(deviceId)
          ? prev.deviceIds.filter((id) => id !== deviceId)
          : [...prev.deviceIds, deviceId],
      };
    });
  };

  const toggleGroupDeviceSelection = (deviceId) => {
    setGroupSelectedDevices((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  const toggleSelectAllInModel = (modelDevices) => {
    const modelDeviceIds = modelDevices.map((d) => d._id);
    const allSelected = modelDeviceIds.every((id) =>
      groupSelectedDevices.has(id),
    );

    setGroupSelectedDevices((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        modelDeviceIds.forEach((id) => next.delete(id));
      } else {
        modelDeviceIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const assignGroupDevices = () => {
    if (groupSelectedDevices.size === 0) {
      addToast("Lütfen en az bir ürün seçin", "warning");
      return;
    }

    // Validate all devices before assigning
    const devicesToAssign = [];
    const failedDevices = [];

    groupSelectedDevices.forEach((deviceId) => {
      const isCurrentlyAssigned = delivery.deviceIds.includes(deviceId);
      if (!isCurrentlyAssigned) {
        const validation = canAssignDevice(deviceId);
        if (validation.canAssign) {
          devicesToAssign.push(deviceId);
        } else {
          failedDevices.push(deviceId);
        }
      } else {
        devicesToAssign.push(deviceId); // Allow toggling already assigned devices
      }
    });

    // Assign valid devices
    devicesToAssign.forEach((deviceId) => {
      toggleDeviceAssignment(deviceId, true);
    });

    // Show messages
    if (devicesToAssign.length > 0) {
      addToast(
        `${devicesToAssign.length} ürün sevkiyata atandı${
          failedDevices.length > 0
            ? `. ${failedDevices.length} ürün limit nedeniyle atanamadı`
            : ""
        }`,
        failedDevices.length > 0 ? "warning" : "success",
      );
    } else if (failedDevices.length > 0) {
      addToast("Seçilen ürünler model limitleri nedeniyle atanamadı", "danger");
    }

    setGroupSelectedDevices(new Set());
    setGroupAssignMode(false);
  };

  const toggleModelExpansion = (modelId) => {
    setExpandedModelsInDelivery((prev) => {
      const next = new Set(prev);
      if (next.has(modelId)) {
        next.delete(modelId);
      } else {
        next.add(modelId);
      }
      return next;
    });
  };

  const validateForm = (sequenceOverride) => {
    const sequenceToUse = sequenceOverride ?? delivery.sequence;
    if (!formData.companyId) {
      addToast("Lütfen bir cari seçin", "warning");
      return false;
    }
    if (!formData.saleId) {
      addToast("Lütfen bir sipariş seçin", "warning");
      return false;
    }
    const selectedSale = sales.find((s) => s._id === formData.saleId);
    if (!transferId && selectedSale?.deliveryApproval?.allowed === false) {
      addToast(
        selectedSale.deliveryApproval.reason ||
          "Bu sipariş için sevkiyat onayı tamamlanmalıdır.",
        "warning",
      );
      return false;
    }
    const fallbackDeliveryName =
      selectedSale?.saleName && sequenceToUse
        ? `${selectedSale.saleName}-${sequenceToUse}`
        : "";
    const deliveryNameToUse = delivery.deliveryName?.trim()
      ? delivery.deliveryName
      : fallbackDeliveryName;

    if (!deliveryNameToUse || deliveryNameToUse.trim() === "") {
      addToast("Lütfen sevkiyat adını girin", "warning");
      return false;
    }
    if (!delivery.deliveredName || delivery.deliveredName.trim() === "") {
      addToast("Lütfen teslim alan adını girin", "warning");
      return false;
    }
    if (!delivery.vehiclePlate || delivery.vehiclePlate.trim() === "") {
      addToast("Lütfen araç plakasını girin", "warning");
      return false;
    }
    return true;
  };
  const handleSaveConfirm = () => {
    const selectedSale = sales.find((s) => s._id === formData.saleId);
    const allDeviceIds = delivery.deviceIds;
    if (allDeviceIds.length > selectedSale?.totalDevices) {
      confirm({
        title: "Uyarı",
        message:
          "Teslim edilecek ürün sayısı siparişta belirtilen ürün sayısından fazla. Sevkiyatı kaydetmek istediğinizden emin misiniz?",
        onConfirm: () => {
          handleSave();
        },
      });
    } else {
      handleSave();
    }
  };

  const handleSave = async () => {
    let sequenceToUse = delivery.sequence;
    if (!transferId && !sequenceToUse && formData.saleId) {
      try {
        const seq = await fetchDeliverySequenceForSale(formData.saleId);
        sequenceToUse = seq.toString();
        setDelivery((prev) => ({ ...prev, sequence: sequenceToUse }));
      } catch (e) {
        console.error("Failed to ensure delivery sequence:", e);
      }
    }

    if (!validateForm(sequenceToUse)) return;

    try {
      setLoading(true);

      const selectedSale = sales.find((s) => s._id === formData.saleId);
      const sequencePadded = sequenceToUse ? sequenceToUse.toString() : "";
      const fallbackDeliveryName =
        selectedSale?.saleName && sequenceToUse
          ? `${selectedSale.saleName}-${sequenceToUse}`
          : "";
      const deliveryNameToUse = delivery.deliveryName?.trim()
        ? delivery.deliveryName
        : fallbackDeliveryName;
      const saleDate = selectedSale?.saleDate
        ? new Date(selectedSale.saleDate).toISOString()
        : new Date().toISOString();

      const allDeviceIds = delivery.deviceIds;

      const devicesPayload = allDeviceIds.map((deviceId) => {
        const device = devices.find((d) => d._id === deviceId);
        return {
          deviceId: deviceId,
          serialNumber: device?.serialNumber || "",
        };
      });

      if (devicesPayload.length === 0) {
        addToast("Lütfen en az bir ürünü sevkiyata atayın", "warning");
        setLoading(false);
        return;
      }

      const payload = {
        saleId: formData.saleId || null,
        deliveryName: deliveryNameToUse || "",
        notes: formData.notes || "",
        transferDate: saleDate,
        deliveryDate: delivery.deliveryDate
          ? new Date(delivery.deliveryDate).toISOString()
          : new Date().toISOString(),
        deliveryTime:
          delivery.deliveryTime || new Date().toTimeString().slice(0, 5),
        driverName: delivery.deliveredName || "",
        vehiclePlate: delivery.vehiclePlate || "",
        devices: devicesPayload,
      };

      if (allDeviceIds.length > selectedSale?.totalDevices) {
        confirm({
          title: "Uyarı",
          message:
            "Teslim edilecek ürün sayısı siparişta belirtilen ürün sayısından fazla. Sevkiyatı kaydetmek istediğinizden emin misiniz?",
          onConfirm: () => {},
          onCancel: () => {
            return;
          },
          confirmButtonText: "Sevkiyatı Kaydet",
        });
      }
      const token = getToken();
      if (transferId) {
        await axios.put(`${API_URL}/api/deliveries/${transferId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        addToast("Sevkiyat başarıyla güncellendi", "success");
      } else {
        await axios.post(`${API_URL}/api/deliveries`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        addToast("Sevkiyat başarıyla oluşturuldu", "success");
      }

      setTimeout(() => navigate("/deliveries"), 1000);
    } catch (error) {
      console.error("Save error:", error);
      addToast(
        error.response?.data?.message || "Sevkiyat kaydedilemedi",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  // Check if trying to assign more devices than expected
  useEffect(() => {
    const selectedSale = sales.find((s) => s._id === formData.saleId);
    const expectedDeviceCount =
      selectedSale?.totalDevices || selectedSale?.devices?.length || 0;
    const assignedDeviceCount = delivery.deviceIds.length;

    if (expectedDeviceCount > 0 && assignedDeviceCount > expectedDeviceCount) {
      addToast(
        "Tüm ürünler atandı. Daha fazla ürün teslim etmek için Siparişi düzenleyin",
        "warning",
      );
    }
  }, [delivery, formData.saleId, sales, addToast]);

  // Helper function to get previously delivered count excluding current delivery
  const getPreviouslyDeliveredCount = (modelId, modelDeliveredCount) => {
    if (!transferId || originalDeviceIds.length === 0) {
      return modelDeliveredCount || 0;
    }

    // Count devices from original delivery that match this model
    const originalDevicesForModel = originalDeviceIds.filter((deviceId) => {
      const device = devices.find((d) => d._id === deviceId);
      if (!device) return false;
      const deviceModelId = (device.modelId?._id || device.modelId)?.toString();
      return deviceModelId === modelId?.toString();
    }).length;

    // Subtract original delivery devices from delivered count
    return Math.max(0, (modelDeliveredCount || 0) - originalDevicesForModel);
  };

  // Helper function to format sale display text
  const formatSaleDisplay = (sale) => {
    if (!sale) return "Sipariş seçin";

    const saleName = sale.saleName || "";
    const companyName = sale.companyId?.name || "";
    const companyNameWords = companyName.split(" ").slice(0, 3).join(" ");
    const saleDate = sale.saleDate
      ? formatInstantDateShortTr(sale.saleDate)
      : "";

    const parts = [saleName];
    if (companyNameWords) {
      parts.push(companyNameWords);
    }
    if (saleDate) {
      parts.push(saleDate);
    }

    return parts.join(" - ");
  };

  const renderFormContent = () => {
    const selectedSale = sales.find((s) => s._id === formData.saleId);
    const selectedCompany = companies.find((c) => c._id === formData.companyId);
    const salesForSelectedCompany = selectedCompany
      ? sales.filter((sale) => {
          const saleCompanyId =
            typeof sale.companyId === "object"
              ? sale.companyId?._id
              : sale.companyId;
          return (
            saleCompanyId?.toString() === selectedCompany._id &&
            (transferId ||
              sale.deliveryApproval?.allowed !== false ||
              sale._id === formData.saleId)
          );
        })
      : [];
    const groupedByModel = groupDevicesByModel(
      filteredDevices.map((d) => d._id),
    );
    const expectedDeviceCount =
      selectedSale?.totalDevices || selectedSale?.devices?.length || 0;
    const assignedDeviceCount = delivery.deviceIds.length;
    const allDeviceIds = delivery.deviceIds;

    return (
      <div className="form-content">
        {/* Sale Information Section */}
        <div className="section-container">
          <div className="section-header">
            <div className="section-icon">
              <FiShoppingCart className="text-primary" />
            </div>
            <div className="section-title flex-grow-1">
              <h5 className="mb-1">Sipariş Bilgileri</h5>
              <p className="text-muted small mb-0">
                Sevkiyat için sipariş seçin ve detayları girin
              </p>
            </div>
          </div>

          <div className="section-content">
            <div className="row g-3">
              <div className="col-12 mb-2">
                <Label required>Firma Seç</Label>
                <div className="form-control-container">
                  <Dropdown
                    required
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2">
                        <span className="dropdown-text">
                          {selectedCompany
                            ? `${selectedCompany.name} (${selectedCompany.code})`
                            : "Firma seçin"}
                        </span>
                      </div>
                    }
                  >
                    <DropdownContent>
                      {companies && companies.length > 0 ? (
                        companies.map((company) => (
                          <DropdownItem
                            key={company._id}
                            onClick={() => handleCompanySelect(company)}
                            active={company._id === formData.companyId}
                          >
                            {company.name} ({company.code})
                          </DropdownItem>
                        ))
                      ) : (
                        <DropdownItem disabled>Firma bulunamadı</DropdownItem>
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>

              <div className="col-12 col-md-6 mb-2">
                <Label required>Sipariş Seç</Label>
                <div className="form-control-container">
                  <Dropdown
                    trigger={
                      <div className="d-flex align-items-center justify-content-between p-2">
                        <span className="dropdown-text">
                          {formatSaleDisplay(selectedSale)}
                        </span>
                      </div>
                    }
                  >
                    <DropdownContent>
                      {formData.companyId ? (
                        salesForSelectedCompany
                          .filter((sale) => sale.status !== "completed")
                          .map((sale) => (
                            <DropdownItem
                              key={sale._id}
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  saleId: sale._id,
                                }))
                              }
                              active={formData.saleId === sale._id}
                            >
                              {formatSaleDisplay(sale)}
                            </DropdownItem>
                          ))
                      ) : (
                        <DropdownItem disabled>Önce cari seçin</DropdownItem>
                      )}
                    </DropdownContent>
                  </Dropdown>
                </div>
              </div>

              <div className="col-12">
                <Input
                  type="textarea"
                  label="Notlar"
                  value={formData.notes}
                  onChange={(value) =>
                    setFormData({ ...formData, notes: value })
                  }
                  placeholder="Not girin..."
                  rows={4}
                />
              </div>

              {selectedSale && (
                <div className="col-12 mt-4">
                  <div className="summary-card">
                    <div className="summary-row">
                      <span className="summary-label">
                        Daha Önce Teslim Edilen:
                      </span>
                      <strong className="summary-value text-muted">
                        {selectedSale.models?.reduce((sum, model) => {
                          const modelId = model.modelId?._id || model.modelId;
                          const previouslyDelivered =
                            getPreviouslyDeliveredCount(
                              modelId,
                              model.deliveredCount,
                            );
                          return sum + previouslyDelivered;
                        }, 0) || 0}
                      </strong>
                    </div>
                    <div className="summary-row">
                      <span className="summary-label">Bu Sevkiyatta:</span>
                      <strong className="summary-value">
                        {allDeviceIds.length}
                      </strong>
                    </div>
                    <div
                      className="summary-row"
                      style={{
                        borderTop: "1px solid #e9ecef",
                        paddingTop: "0.75rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <span
                        className="summary-label"
                        style={{ fontWeight: 600 }}
                      >
                        Toplam Teslim Edilen:
                      </span>
                      <strong
                        className="summary-value"
                        style={{ fontSize: "18px" }}
                      >
                        {(selectedSale.models?.reduce((sum, model) => {
                          const modelId = model.modelId?._id || model.modelId;
                          const previouslyDelivered =
                            getPreviouslyDeliveredCount(
                              modelId,
                              model.deliveredCount,
                            );
                          return sum + previouslyDelivered;
                        }, 0) || 0) + allDeviceIds.length}{" "}
                        / {expectedDeviceCount || 0}
                      </strong>
                    </div>
                    {selectedSale.models && selectedSale.models.length > 0 && (
                      <div className="summary-models-breakdown">
                        <span className="summary-label mb-2 d-block">
                          Sipariş Detayları:
                        </span>
                        {selectedSale.models.map((modelItem, idx) => {
                          const modelId =
                            modelItem.modelId?._id || modelItem.modelId;
                          const modelName =
                            modelItem.modelId?.name || "Bilinmeyen Model";
                          const expectedCount = modelItem.count || 0;
                          const previouslyDelivered =
                            getPreviouslyDeliveredCount(
                              modelId,
                              modelItem.deliveredCount,
                            );

                          // Calculate assigned devices for this model in current delivery
                          const assignedForModel = allDeviceIds.filter(
                            (deviceId) => {
                              const device = devices.find(
                                (d) => d._id === deviceId,
                              );
                              if (!device) return false;
                              const deviceModelId = (
                                device.modelId?._id || device.modelId
                              )?.toString();
                              return deviceModelId === modelId?.toString();
                            },
                          ).length;

                          const totalForModel =
                            previouslyDelivered + assignedForModel;

                          return (
                            <div key={idx} className="summary-model-row">
                              <span className="summary-model-name">
                                {modelName}:
                              </span>
                              <strong
                                className="summary-model-value"
                                style={{
                                  color:
                                    totalForModel >= expectedCount
                                      ? "#10b981"
                                      : totalForModel > 0
                                        ? "#f59e0b"
                                        : "#6c757d",
                                }}
                              >
                                {previouslyDelivered > 0
                                  ? `${
                                      Number(previouslyDelivered) +
                                      Number(assignedForModel)
                                    }`
                                  : assignedForModel}{" "}
                                / {expectedCount}
                              </strong>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delivery Section */}
        <div className="section-container">
          <div className="section-header">
            <div className="section-icon">
              <FiTruck className="text-info" />
            </div>
            <div className="section-title flex-grow-1">
              <h5 className="mb-1">Sevkiyat Bilgileri</h5>
              <p className="text-muted small mb-0">
                Sevkiyat detaylarını girin ve ürünleri atayın
              </p>
            </div>
          </div>

          <div className="section-content">
            {expectedDeviceCount > 0 && (
              <div className="device-stats-bar">
                <>
                  {selectedSale?.models?.reduce((sum, model) => {
                    const modelId = model.modelId?._id || model.modelId;
                    return (
                      sum +
                      getPreviouslyDeliveredCount(modelId, model.deliveredCount)
                    );
                  }, 0) > 0 && (
                    <div className="stat-item">
                      <FiCheckCircle
                        className="stat-icon"
                        style={{ color: "#6c757d" }}
                      />
                      <span className="stat-text" style={{ color: "#6c757d" }}>
                        {selectedSale.models.reduce((sum, model) => {
                          const modelId = model.modelId?._id || model.modelId;
                          return (
                            sum +
                            getPreviouslyDeliveredCount(
                              modelId,
                              model.deliveredCount,
                            )
                          );
                        }, 0)}{" "}
                        Önceden Teslim
                      </span>
                    </div>
                  )}
                  <div
                    className={`stat-item progress-indicator ${
                      (selectedSale?.models?.reduce((sum, model) => {
                        const modelId = model.modelId?._id || model.modelId;
                        return (
                          sum +
                          getPreviouslyDeliveredCount(
                            modelId,
                            model.deliveredCount,
                          )
                        );
                      }, 0) || 0) +
                        assignedDeviceCount <
                      expectedDeviceCount
                        ? "warning"
                        : "success"
                    }`}
                  >
                    <FiCheckCircle className="stat-icon" />
                    <span className="stat-text">
                      {(selectedSale?.models?.reduce((sum, model) => {
                        const modelId = model.modelId?._id || model.modelId;
                        return (
                          sum +
                          getPreviouslyDeliveredCount(
                            modelId,
                            model.deliveredCount,
                          )
                        );
                      }, 0) || 0) + assignedDeviceCount}
                      /{expectedDeviceCount} Toplam
                    </span>
                  </div>
                </>
              </div>
            )}

            {expectedDeviceCount > 0 &&
              (selectedSale?.models?.reduce((sum, model) => {
                const modelId = model.modelId?._id || model.modelId;
                return (
                  sum +
                  getPreviouslyDeliveredCount(modelId, model.deliveredCount)
                );
              }, 0) || 0) +
                assignedDeviceCount >
                expectedDeviceCount && (
                <div className="alert-banner warning">
                  <FiAlertCircle className="alert-icon" />
                  <span className="alert-text">
                    Uyarı: Beklenen ürün sayısından ({expectedDeviceCount})
                    fazla ürün atandı (
                    {(selectedSale?.models?.reduce((sum, model) => {
                      const modelId = model.modelId?._id || model.modelId;
                      return (
                        sum +
                        getPreviouslyDeliveredCount(
                          modelId,
                          model.deliveredCount,
                        )
                      );
                    }, 0) || 0) + assignedDeviceCount}
                    )
                  </span>
                </div>
              )}

            {/* Delivery Fields */}
            <div className="delivery-fields-container mb-4">
              <div className="mb-3 ">
                <Input
                  type="text"
                  label="Sevkiyat Adı"
                  required
                  value={delivery.deliveryName}
                  placeholder="Sevkiyat adı *"
                />
              </div>
              <div className="d-flex gap-1">
                <div className="w-50">
                  <Input
                    type="text"
                    label="Teslim Alan Adı"
                    required
                    value={delivery.deliveredName}
                    onChange={(value) =>
                      updateDeliveryField("deliveredName", value)
                    }
                    placeholder="Teslim alan adı *"
                  />
                </div>
                <div className="w-50">
                  <Input
                    type="text"
                    label="Araç Plakası"
                    required
                    value={delivery.vehiclePlate}
                    onChange={(value) =>
                      updateDeliveryField("vehiclePlate", value)
                    }
                    placeholder="Araç plakası *"
                  />
                </div>
              </div>
              <div className="d-flex gap-1">
                <div className="w-50">
                  <Input
                    type="date"
                    label="Sevkiyat Tarihi"
                    value={delivery.deliveryDate}
                    onChange={(value) =>
                      updateDeliveryField("deliveryDate", value)
                    }
                  />
                </div>
                <div className="w-50">
                  <Input
                    type="time"
                    label="Sevkiyat Saati"
                    value={delivery.deliveryTime}
                    onChange={(value) =>
                      updateDeliveryField("deliveryTime", value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Assigned Devices */}
            {delivery.deviceIds.length > 0 && (
              <div className="assigned-devices-section">
                <h6 className="section-subtitle">Seçilenler</h6>
                <div className="model-groups-list">
                  {(() => {
                    const deliveryGrouped = groupDevicesByModel(
                      delivery.deviceIds,
                    );
                    return Object.entries(deliveryGrouped).map(
                      ([modelId, { modelName, devices: modelDevices }]) => {
                        const isModelExpanded =
                          expandedModelsInDelivery.has(modelId);

                        return (
                          <div key={modelId} className="model-group-card">
                            <div
                              className="model-group-header"
                              onClick={() => toggleModelExpansion(modelId)}
                            >
                              <div className="model-icon-wrapper-small">
                                <FiBox />
                              </div>
                              <div className="model-info flex-grow-1">
                                <div className="model-name-text">
                                  {modelName}
                                </div>
                                <div className="model-count-text">
                                  {modelDevices.length} Ürün
                                </div>
                              </div>
                              <IconButton
                                icon={
                                  isModelExpanded ? (
                                    <FiChevronUp />
                                  ) : (
                                    <FiChevronDown />
                                  )
                                }
                                appearance="subtle"
                                size="sm"
                              />
                            </div>

                            {isModelExpanded && (
                              <div className="model-devices-grid">
                                {modelDevices.map((device) => (
                                  <div key={device._id} className="device-chip">
                                    <span className="device-serial">
                                      {device.serialNumber}
                                    </span>
                                    <IconButton
                                      icon={<FiX />}
                                      appearance="danger-subtle"
                                      size="sm"
                                      onClick={() =>
                                        toggleDeviceAssignment(device._id)
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      },
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Device Assignment Section */}
        <div className="section-container">
          <div className="section-header">
            <div className="section-icon">
              <FiServer className="text-success" />
            </div>
            <div className="section-title flex-grow-1">
              <h5 className="mb-1">Stoktaki Ürünler</h5>
              <p className="text-muted small mb-0">Sevkiyat için ürün seçin</p>
            </div>
            <div className="stat-item">
              <FiPackage className="stat-icon" />
              <span className="stat-text">
                {filteredDevices.length} Stoktaki Ürün
              </span>
            </div>
            <div className="header-actions-group">
              {groupSelectedDevices.size > 0 && (
                <Lozenge appearance="primary">
                  {groupSelectedDevices.size} seçili
                </Lozenge>
              )}
              <Button
                appearance={groupAssignMode ? "primary" : "primary-subtle"}
                onClick={() => setGroupAssignMode(!groupAssignMode)}
              >
                Toplu Seçim
              </Button>
            </div>
          </div>

          <div className="section-content">
            {groupAssignMode && groupSelectedDevices.size > 0 && (
              <div className="group-assign-panel">
                <div className="panel-header">
                  <FiCheckCircle className="panel-icon" />
                  <p className="panel-text">
                    {groupSelectedDevices.size} ürün seçildi
                  </p>
                </div>
                <div className="panel-actions">
                  <Button
                    appearance="primary"
                    icon={<FiCheck />}
                    onClick={() => assignGroupDevices()}
                  >
                    Sevkiyata Ekle
                  </Button>
                  <Button
                    appearance="danger-subtle"
                    onClick={() => {
                      setGroupSelectedDevices(new Set());
                      setGroupAssignMode(false);
                    }}
                  >
                    İptal
                  </Button>
                </div>
              </div>
            )}

            <div className="models-list">
              {Object.entries(groupedByModel).map(
                ([modelId, { modelName, devices: modelDevices }]) => {
                  const isModelExpanded = expandedModelsInDelivery.has(modelId);
                  const unassignedModelDevices = modelDevices.filter(
                    (d) => !delivery.deviceIds.includes(d._id),
                  );

                  if (unassignedModelDevices.length === 0) return null;

                  return (
                    <div key={modelId} className="model-section-card">
                      <div
                        className="model-section-header"
                        onClick={() => toggleModelExpansion(modelId)}
                      >
                        <div className="model-icon-wrapper-small">
                          <FiBox />
                        </div>
                        <div className="model-section-info flex-grow-1">
                          <div className="model-name-text">{modelName}</div>
                          <div className="model-count-text">
                            {unassignedModelDevices.length} ürün
                          </div>
                        </div>
                        <div className="model-section-actions">
                          {groupAssignMode && (
                            <input
                              type="checkbox"
                              checked={modelDevices.every((d) =>
                                groupSelectedDevices.has(d._id),
                              )}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectAllInModel(modelDevices);
                              }}
                              className="form-check-input"
                            />
                          )}
                          <IconButton
                            icon={
                              isModelExpanded ? (
                                <FiChevronUp />
                              ) : (
                                <FiChevronDown />
                              )
                            }
                            appearance="subtle"
                            size="sm"
                          />
                        </div>
                      </div>

                      {isModelExpanded && (
                        <div className="devices-grid-section">
                          {unassignedModelDevices.map((device) => {
                            const isAssigned = delivery.deviceIds.includes(
                              device._id,
                            );

                            return (
                              <div
                                key={device._id}
                                className={`device-assignment-card ${
                                  groupSelectedDevices.has(device._id)
                                    ? "selected"
                                    : ""
                                } ${isAssigned ? "assigned" : ""}`}
                              >
                                <div
                                  className="device-card-main"
                                  onClick={() => {
                                    if (groupAssignMode) {
                                      toggleGroupDeviceSelection(device._id);
                                    } else {
                                      toggleDeviceAssignment(device._id);
                                    }
                                  }}
                                >
                                  {groupAssignMode && (
                                    <input
                                      type="checkbox"
                                      checked={groupSelectedDevices.has(
                                        device._id,
                                      )}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        toggleGroupDeviceSelection(device._id);
                                      }}
                                      className="form-check-input device-checkbox"
                                    />
                                  )}
                                  <div className="device-info-section">
                                    <div className="device-serial-text">
                                      {device.serialNumber}
                                    </div>
                                    {device.platform?.name && (
                                      <div className="device-platform-text">
                                        {device.platform.name}
                                      </div>
                                    )}
                                    {isAssigned && !groupAssignMode && (
                                      <div className="device-assigned-badge">
                                        <FiCheckCircle className="me-1" />
                                        Atandı
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Block access to create/edit form if user has no write permission
  if (!canWrite) {
    return (
      <AppPage
        title={translate("UI.DELIVERIES") || "Sevkiyatlar"}
        subtitle={
          translate("UI.NO_WRITE_ACCESS") ||
          "Bu sayfada değişiklik yapma yetkiniz yok."
        }
      >
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>
                {translate("UI.NO_WRITE_ACCESS") ||
                  "Bu sevkiyatta değişiklik yapma yetkiniz yok."}
              </span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/deliveries")}
              >
                {translate("UI.BACK") || "Geri Dön"}
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={transferId ? "Sevkiyati Düzenle" : "Yeni Sevkiyat"}
      breadcrumbs={breadcrumbs}
    >
      <style>
        {`
          .app-page-header {
            margin-bottom: 1.5rem;
          }
        `}
      </style>
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm modern-card">
            <div className="card-body p-4">
              {renderFormContent()}

              {/* Form Actions */}
              <div className="form-actions">
                <Button
                  appearance="danger-subtle"
                  onClick={() => navigate(`/deliveries`)}
                >
                  İptal
                </Button>
                <Button
                  appearance="primary-animated"
                  onClick={handleSaveConfirm}
                  loading={loading}
                  icon={<FiCheck />}
                >
                  Sevkiyat Kaydet
                </Button>
              </div>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <ToastContainer>
        {toasts?.map((toast, index) => (
          <Toast
            key={toast.id}
            description={toast.message}
            title={
              toast.type === "danger"
                ? "Hata!"
                : toast.type === "success"
                  ? "Başarılı!"
                  : "Bilgi"
            }
            appearance={toast.type}
            onDismissed={() => removeToast(index)}
            onClose={() => removeToast(index)}
            autoDismiss={true}
          />
        ))}
      </ToastContainer>

      <style>
        {`
          .modern-card {
            border-radius: 12px;
            border: 1px solid #e9ecef;
            overflow: hidden;
          }

          .section-container {
            margin-bottom: 2rem;
            padding: 1.75rem;
            background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
            border-radius: 12px;
            border: 1px solid #e9ecef;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
            transition: all 0.3s ease;
          }

          .section-container:hover {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          }

          .section-container:last-child {
            margin-bottom: 0;
          }

          .section-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.75rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid #f1f3f5;
          }

          .section-icon {
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
            flex-shrink: 0;
          }

          .section-icon svg {
            width: 24px;
            height: 24px;
            color: white;
          }

          .section-title {
            flex: 1;
          }

          .section-title h5 {
            font-size: 1.15rem;
            font-weight: 600;
            margin: 0 0 0.25rem 0;
            color: #1a202c;
          }

          .section-title p {
            margin: 0;
            line-height: 1.4;
          }

          .section-content {
            padding: 0.5rem 0 0 0;
          }

          .form-control-container {
            background: #fff;
            border: 1px solid #e2e5e8;
            border-radius: 8px;
            transition: all 0.2s ease;
            cursor: pointer;
          }

          .form-control-container:hover {
            border-color: #00b4f3;
            box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1);
          }

          .dropdown-text {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .summary-card {
            padding: 1.25rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border: 1px solid #e9ecef;
            border-radius: 12px;
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .summary-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .summary-label {
            font-size: 14px;
            color: #6c757d;
            font-weight: 500;
          }

          .summary-value {
            font-size: 16px;
            font-weight: 600;
            color: #00b4f3;
          }

          .summary-models-breakdown {
            margin-top: 0.5rem;
            padding-top: 0.75rem;
            border-top: 1px solid #e9ecef;
          }

          .summary-model-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            font-size: 14px;
          }

          .summary-model-name {
            color: #6c757d;
            font-weight: 500;
          }

          .summary-model-value {
            font-size: 14px;
            font-weight: 600;
            color: #00b4f3;
          }

          .search-container {
            margin-bottom: 1rem;
          }

          .device-stats-bar {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border: 1px solid #e9ecef;
            border-radius: 10px;
            margin-bottom: 1.5rem;
          }

          .stat-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .stat-icon {
            width: 20px;
            height: 20px;
            color: #00b4f3;
          }

          .stat-text {
            font-size: 14px;
            font-weight: 600;
            color: #1a202c;
          }

          .progress-indicator.warning .stat-icon {
            color: #f59e0b;
          }

          .progress-indicator.success .stat-icon {
            color: #10b981;
          }

          .alert-banner {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem 1.25rem;
            margin-top: 1rem;
            border-radius: 10px;
            background: #fff3cd;
            border: 1px solid #ffc107;
          }

          .alert-banner.warning {
            background: #fff3cd;
            border-color: #ffc107;
          }

          .alert-icon {
            width: 20px;
            height: 20px;
            color: #f59e0b;
            flex-shrink: 0;
          }

          .alert-text {
            font-size: 14px;
            font-weight: 600;
            color: #856404;
            flex: 1;
          }

          .delivery-fields-container {
            padding: 1.5rem;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 12px;
            margin-top: 1rem;
          }

          .deliveries-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .delivery-card {
            border: 1px solid #e9ecef;
            border-radius: 12px;
            background: #fff;
            overflow: hidden;
            transition: all 0.3s ease;
          }

          .delivery-card:hover {
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
          }

          .delivery-card-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1.25rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
            border-bottom: 1px solid #e9ecef;
          }

          .delivery-number-badge {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #00b4f3 0%, #006C80 100%);
            color: white;
            border-radius: 10px;
            font-weight: 700;
            font-size: 18px;
            flex-shrink: 0;
            box-shadow: 0 4px 12px rgba(12, 132, 121, 0.2);
          }

          .delivery-main-info {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .delivery-name-display {
            font-size: 16px;
            font-weight: 600;
            color: #1a202c;
            padding: 0.5rem 0;
          }

          .delivery-meta {
            display: flex;
            gap: 0.5rem;
            align-items: center;
          }

          .delivery-card-body {
            padding: 1.25rem;
          }

          .assigned-devices-section {
            padding: 1.25rem;
            background: #f8f9fa;
            border-top: 1px solid #e9ecef;
          }

          .section-subtitle {
            font-size: 14px;
            font-weight: 600;
            color: #1a202c;
            margin-bottom: 1rem;
          }

          .model-groups-list {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .model-group-card {
            border: 1px solid #e9ecef;
            border-radius: 10px;
            background: #fff;
            overflow: hidden;
          }

          .model-group-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .model-group-header:hover {
            background: #f8f9fa;
          }

          .model-icon-wrapper-small {
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%);
            border-radius: 8px;
            flex-shrink: 0;
          }

          .model-icon-wrapper-small svg {
            width: 18px;
            height: 18px;
            color: #6c757d;
          }

          .model-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .model-name-text {
            font-size: 14px;
            font-weight: 600;
            color: #1a202c;
          }

          .model-count-text {
            font-size: 12px;
            color: #6c757d;
          }

          .model-devices-grid {
            padding: 0 1rem 1rem;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 0.5rem;
          }

          .device-chip {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .device-chip:hover {
            background: #fff;
            border-color: var(--m-primary);
          }

          .device-serial {
            font-size: 13px;
            font-weight: 600;
            color: #1a202c;
          }

          .header-actions-group {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .group-assign-panel {
            padding: 1.25rem;
            background: var(--m-primary-subtle);
            border: 2px solid var(--m-primary);
            border-radius: 12px;
            margin-bottom: 1.5rem;
          }

          .panel-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
          }

          .panel-icon {
            width: 24px;
            height: 24px;
            color: var(--m-primary);
          }

          .panel-text {
            margin: 0;
            font-size: 14px;
            font-weight: 600;
            color: var(--m-primary);
          }

          .panel-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .models-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }

          .model-section-card {
            border: 1px solid #e9ecef;
            border-radius: 12px;
            background: #fff;
            overflow: hidden;
          }

          .model-section-header {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .model-section-header:hover {
            background: #f8f9fa;
          }

          .model-section-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .model-section-actions {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .devices-grid-section {
            padding: 1rem;
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1rem;
            background: #f8f9fa;
          }

          .device-assignment-card {
            border: 1px solid #e9ecef;
            border-radius: 10px;
            background: #fff;
            overflow: hidden;
            transition: all 0.2s ease;
          }

          .device-assignment-card:hover {
            border-color: #00b4f3;
            box-shadow: 0 4px 12px rgba(12, 132, 121, 0.15);
          }

          .device-assignment-card.selected {
            border-color: var(--m-primary);
            background: #eff6ff;
          }

          .device-card-main {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 1rem;
          }

          .device-checkbox {
            flex-shrink: 0;
          }

          .device-info-section {
            display: flex;
            flex-direction: column;
            gap: 4px;
            flex: 1;
          }

          .device-serial-text {
            font-size: 14px;
            font-weight: 600;
            color: #1a202c;
          }

          .device-platform-text {
            font-size: 12px;
            color: #6c757d;
          }

          .quick-assign-buttons {
            display: flex;
            gap: 4px;
            padding: 0.5rem 1rem 1rem;
            background: #f8f9fa;
            border-top: 1px solid #e9ecef;
          }

          .quick-assign-button {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem;
            background: #fff;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 600;
            color: #6c757d;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .quick-assign-button:hover {
            background: #00b4f3;
            color: white;
            border-color: #00b4f3;
          }

          .quick-assign-button.active {
            background: #00b4f3;
            color: white;
            border-color: #00b4f3;
          }

          .form-actions {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 1rem;
            margin-top: 0;
            padding: 1.5rem;
            border-top: 2px solid #f1f3f5;
            border-radius: 0 0 12px 12px;
          }

          @media (max-width: 768px) {
            .section-container {
              padding: 1.25rem;
            }

            .section-icon {
              width: 42px;
              height: 42px;
            }

            .section-title h5 {
              font-size: 1rem;
            }

            .devices-grid-section {
              grid-template-columns: 1fr;
            }

            .form-actions {
              flex-direction: column-reverse;
              gap: 0.75rem;
            }

            .form-actions button {
              width: 100%;
            }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .section-container {
            animation: slideIn 0.4s ease-out;
          }

          input:focus,
          textarea:focus,
          select:focus {
            outline: none;
            border-color: #00b4f3 !important;
            box-shadow: 0 0 0 3px rgba(12, 132, 121, 0.1) !important;
          }
        `}
      </style>
    </AppPage>
  );
};

export default withAuth(StockTransferForm, ["admin", "employee", "user"]);
